import React, { useState, useEffect } from "react";
import axios from "axios";
import * as XLSX from "xlsx";

function App() {
  const [country, setCountry] = useState(""); // Blank initially
  const [state, setState] = useState(""); // U.S. state
  const [city, setCity] = useState(""); // City
  const [count, setCount] = useState(1);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  // List of countries (including the countries you mentioned)
  const allCountries = [
    "Australia", "Austria", "Belgium", "Brazil", "Canada", "Cyprus (Anglicized)", "Cyprus (Greek)", 
    "Czech Republic", "Denmark", "Estonia", "Finland", "France", "Germany", "Greenland", "Hungary", 
    "Iceland", "Italy", "Netherlands", "New Zealand", "Norway", "Poland", "Portugal", "Slovenia", 
    "South Africa", "Spain", "Sweden", "Switzerland", "Tunisia", "United Kingdom", "United States", "Uruguay"
  ];

  // List of U.S. states and territories
  const usStates = {
    "Alabama": "AL", "Alaska": "AK", "Arizona": "AZ", "Arkansas": "AR", "California": "CA", 
    "Colorado": "CO", "Connecticut": "CT", "Delaware": "DE", "Florida": "FL", "Georgia": "GA", 
    "Hawaii": "HI", "Idaho": "ID", "Illinois": "IL", "Indiana": "IN", "Iowa": "IA", "Kansas": "KS", 
    "Kentucky": "KY", "Louisiana": "LA", "Maine": "ME", "Maryland": "MD", "Massachusetts": "MA", 
    "Michigan": "MI", "Minnesota": "MN", "Mississippi": "MS", "Missouri": "MO", "Montana": "MT", 
    "Nebraska": "NE", "Nevada": "NV", "New Hampshire": "NH", "New Jersey": "NJ", "New Mexico": "NM", 
    "New York": "NY", "North Carolina": "NC", "North Dakota": "ND", "Ohio": "OH", "Oklahoma": "OK", 
    "Oregon": "OR", "Pennsylvania": "PA", "Rhode Island": "RI", "South Carolina": "SC", "South Dakota": "SD", 
    "Tennessee": "TN", "Texas": "TX", "Utah": "UT", "Vermont": "VT", "Virginia": "VA", "Washington": "WA", 
    "West Virginia": "WV", "Wisconsin": "WI", "Wyoming": "WY", "District of Columbia": "DC", "American Samoa": "AS", 
    "Guam": "GU", "Northern Mariana Islands": "MP", "Puerto Rico": "PR", "United States Minor Outlying Islands": "UM", 
    "Virgin Islands, U.S.": "VI"
  };

  // Mock Data for cities (using basic city names for illustration)
  const countryData = {
    Australia: ["Sydney", "Melbourne", "Brisbane"],
    Austria: ["Vienna", "Salzburg", "Innsbruck"],
    Belgium: ["Brussels", "Antwerp", "Ghent"],
    Brazil: ["Rio de Janeiro", "São Paulo", "Brasília"],
    Canada: ["Toronto", "Vancouver", "Montreal"],
    "Cyprus (Anglicized)": ["Nicosia", "Limassol", "Larnaca"],
    "Cyprus (Greek)": ["Nicosia", "Limassol", "Larnaca"],
    "Czech Republic": ["Prague", "Brno", "Plzeň"],
    Denmark: ["Copenhagen", "Aarhus", "Odense"],
    Estonia: ["Tallinn", "Tartu", "Pärnu"],
    Finland: ["Helsinki", "Tampere", "Espoo"],
    France: ["Paris", "Marseille", "Lyon"],
    Germany: ["Berlin", "Munich", "Hamburg"],
    Greenland: ["Nuuk", "Qaqortoq", "Ilulissat"],
    Hungary: ["Budapest", "Debrecen", "Szeged"],
    Iceland: ["Reykjavik", "Akureyri", "Reykjanesbær"],
    Italy: ["Rome", "Milan", "Naples"],
    Netherlands: ["Amsterdam", "Rotterdam", "Utrecht"],
    "New Zealand": ["Auckland", "Wellington", "Christchurch"],
    Norway: ["Oslo", "Bergen", "Stavanger"],
    Poland: ["Warsaw", "Kraków", "Gdańsk"],
    Portugal: ["Lisbon", "Porto", "Braga"],
    Slovenia: ["Ljubljana", "Maribor", "Celje"],
    "South Africa": ["Cape Town", "Johannesburg", "Durban"],
    Spain: ["Madrid", "Barcelona", "Valencia"],
    Sweden: ["Stockholm", "Gothenburg", "Malmö"],
    Switzerland: ["Zurich", "Geneva", "Bern"],
    Tunisia: ["Tunis", "Sfax", "Sousse"],
    "United Kingdom": ["London", "Manchester", "Birmingham"],
    "United States": ["New York", "Los Angeles", "San Francisco"],
    Uruguay: ["Montevideo", "Salto", "Paysandú"]
  };

  // Automatically select the first city of the selected country
  useEffect(() => {
    if (country && countryData[country]) {
      setCity(countryData[country][0]);
    } else {
      setCity(""); // Reset city when country is blank
    }
  }, [country]);

  // Automatically clear state if the country is not the United States
  useEffect(() => {
    if (country !== "United States") {
      setState(""); // Clear state dropdown if country is not United States
    }
  }, [country]);

  // Handle country change
  const handleCountryChange = (e) => {
    const selectedCountry = e.target.value;
    setCountry(selectedCountry);
    setCity(""); // Clear city when country is changed
  };

  // Handle state change for US
  const handleStateChange = (e) => {
    setState(e.target.value);
  };

  // Handle count change
  const handleCountChange = (e) => {
    setCount(e.target.value);
  };

  // Generate fake data
  const generateData = () => {
    setLoading(true);
    
    // Send request to backend with country and number of records to generate
    axios
      .post("http://localhost:3001/api/fake-details", { country, numRecords: count })
      .then((response) => {
        const generatedData = Array.isArray(response.data) ? response.data : [response.data];
        
        const formattedData = generatedData.map(item => ({
          FirstName: item.FirstName,
          LastName: item.LastName,
          Gender: item.Gender,
          Birthday: item.Birthday,
          Email: item.Email,
          Phone: item.PhoneNumber,
          StreetAddress: item.Address.StreetAddress,
          City: item.Address.City,
          ZipCode: item.Address.ZipCode,
          Country: item.Address.Country,
          State: item.Address.State,  // Include state for countries like US
          UserAgent: item.UserAgent
        }));
  
        // If a state is selected and country is United States, filter by state
        if (state && country === "United States") {
          const filteredData = formattedData.filter(item => item.State === state);
          setData(filteredData);  // Set the filtered data
        } else {
          setData(formattedData);  // Show all data if no state or other countries are selected
        }
  
        setLoading(false);  // Stop loading state
      })
      .catch((error) => {
        console.error("Error generating data:", error);
        setLoading(false);  // Stop loading in case of error
      });
  };
  

  // Export to Excel
  const exportToExcel = () => {
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Fake Data");
    XLSX.writeFile(wb, "fake_data.xlsx");
  };

  return (
    <div className="App" style={{ fontFamily: "Arial, sans-serif", padding: "20px" }}>
      <h1 style={{ textAlign: "center", color: "#333" }}>Fake Data Generator</h1>

      {/* Country Dropdown, State/Province and Count/Input Field in a Single Row */}
      <div style={{ display: "flex", justifyContent: "center", alignItems: "center", marginBottom: "20px" }}>
        {/* Country Dropdown */}
        <div style={{ marginRight: "10px" }}>
          <label style={{ fontSize: "16px", marginRight: "10px" }}>Country: </label>
          <select
            onChange={handleCountryChange}
            value={country}
            style={{
              padding: "8px 10px",
              fontSize: "16px",
              borderRadius: "4px",
              border: "1px solid #ddd",
            }}
          >
            <option value="">Select Country</option> {/* Blank option for default */}
            {allCountries.map((countryName) => (
              <option key={countryName} value={countryName}>
                {countryName}
              </option>
            ))}
          </select>
        </div>

        {/* State Dropdown (Only for United States) */}
        {country === "United States" && (
          <div style={{ marginRight: "10px" }}>
            <label style={{ fontSize: "16px", marginRight: "10px" }}>State: </label>
            <select
              onChange={handleStateChange}
              value={state}
              style={{
                padding: "8px 10px",
                fontSize: "16px",
                borderRadius: "4px",
                border: "1px solid #ddd",
              }}
            >
              <option value="">Select State</option> {/* Blank option for default */}
              {Object.keys(usStates).map((stateName) => (
                <option key={usStates[stateName]} value={usStates[stateName]}>
                  {stateName}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* Count/Input Field */}
        <div style={{ marginRight: "10px" }}>
          <label style={{ fontSize: "16px", marginRight: "10px" }}>Number of Records: </label>
          <input
            type="number"
            value={count}
            onChange={handleCountChange}
            min="1"
            max="100"
            style={{
              padding: "8px 10px",
              fontSize: "16px",
              borderRadius: "4px",
              border: "1px solid #ddd",
              width: "60px",
            }}
          />
        </div>
      </div>

      {/* Generate Data Button */}
      <div style={{ display: "flex", justifyContent: "center" }}>
        <button
          onClick={generateData}
          disabled={loading}
          style={{
            backgroundColor: "#4CAF50",
            color: "white",
            padding: "12px 20px",
            fontSize: "16px",
            border: "none",
            borderRadius: "4px",
            cursor: loading ? "not-allowed" : "pointer",
            transition: "background-color 0.3s",
          }}
        >
          {loading ? "Generating..." : "Generate Data"}
        </button>
      </div>

      {/* Data Table */}
      {data.length > 0 && (
        <div style={{ marginTop: "30px", textAlign: "center" }}>
          <h3 style={{ color: "#333" }}>Generated Fake Data</h3>
          <table
            style={{
              width: "100%",
              borderCollapse: "collapse",
              marginBottom: "20px",
              border: "1px solid #ddd",
            }}
          >
            <thead>
              <tr style={{ backgroundColor: "#f2f2f2" }}>
                {Object.keys(data[0]).map((header) => (
                  <th key={header} style={{ padding: "10px", textAlign: "left" }}>
                    {header}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.map((item, index) => (
                <tr key={index}>
                  {Object.values(item).map((value, i) => (
                    <td key={i} style={{ padding: "10px" }}>
                      {value}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>

          {/* Export Button */}
          <button
            onClick={exportToExcel}
            style={{
              backgroundColor: "#007BFF",
              color: "white",
              padding: "12px 20px",
              fontSize: "16px",
              border: "none",
              borderRadius: "4px",
              cursor: "pointer",
            }}
          >
            Export to Excel
          </button>
        </div>
      )}
    </div>
  );
}

export default App;
